
import { Typography } from "antd";
import { Chart } from "chart.js";
import React, { useEffect, useRef } from "react";
import { ChartFont } from "..";
import { ReportTypeEnum } from "../../../../constants/constants";
import { DashboardEcapModuleModel } from "../../../../models/dashboad/dashboard.model";
import { AMOUNT_UTIL } from "../../../../utils/amount.utils";
import UTIL from "../../../../utils/utils";

interface Props {
    data: DashboardEcapModuleModel[]
}

const DashboardEcapModule = (props: Props) => {

    let chart = useRef<Chart>();
    const forceUpdate = React.useReducer(() => ({}), {})[1] as () => void

    const getChartColor = (label: string) => {
        switch (label) {
            case ReportTypeEnum.CreditRisk:
                return "#9AC4F8";
            case ReportTypeEnum.BusinessRisk:
                return "#A4303F";
            case ReportTypeEnum.ConcentrationRisk:
                return "#718F94";
            case ReportTypeEnum.OperationalRisk:
                return "#EC91D8";
            case ReportTypeEnum.InterestRateRisk:
                return "#41D3BD";
            case "available_capital":
                return "#547EBD";
            default:
                return UTIL.randomColor(label);
        }
    }

    const getChartData = (values: DashboardEcapModuleModel[]) => {
        const datasets: any[] = [];
        let labels: string[] = values.map(x => x.year?.toString());

        if (values && values.length) {
            Object.keys(values[0]).forEach(key => {

                if (key !== "year") {

                    const isAvailableCapital = key === "available_capital";

                    datasets.push({
                        label: isAvailableCapital ? "Available Capital" : UTIL.getECapReportTypeDisplay(key),
                        data: values.map((x: any) => x[key]),
                        backgroundColor: getChartColor(key),
                        hidden: false,
                        barPercentage: 0.7,
                        stack: isAvailableCapital ? 'Stack 0' : 'Stack 1'
                    });
                }

            })
        }

        return {
            labels: labels,
            datasets: datasets
        }
    }

    const createChart = (chartData: any) => {
        const element: any = document.getElementById("eCAPModuleChart");
        const ctx = element.getContext('2d');
        ctx.height = 600;
        chart.current = new Chart(ctx, {
            type: 'bar',
            data: chartData,
            options: {
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            // Include a dollar sign in the ticks
                            callback: function (value, index, values) {
                                return AMOUNT_UTIL.convertNumericToFormattedAmount(Number(value), 0);
                            },
                            font: ChartFont,
                            color: "#969696"
                        }
                    },
                    x: {
                        ticks: {
                            font: ChartFont,
                            color: "#969696"
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false,
                    }
                }
            },
        });

        forceUpdate();
    }

    const updateChart = (chartData: any) => {
        if (chart.current) {
            chart.current.data = chartData;
            chart.current.update();
            forceUpdate();
        }
    }

    const lengendClick = (label: string) => {
        if (chart.current) {
            chart.current.data.datasets.forEach(dataset => {
                if (dataset.label === label) {
                    dataset.hidden = !dataset.hidden;
                }
            });

            chart.current.update();
            forceUpdate();
        }

    }

    useEffect(() => {
        if (props.data && props.data.length) {
            const chartData = getChartData(props.data);
            if (!chart.current) {
                createChart(chartData);
            } else {
                updateChart(chartData);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data])

    return (
        <div className="chart-wrapper">
            <p className="title">
                ECAP Module
            </p>
            <p>
                Required vs. Available Capital ($ in 000’s)
            </p>
            <canvas id="eCAPModuleChart" height="200"></canvas>
            <div className="chart-legend">
                {
                    chart.current?.data.datasets.map((item: any) => {

                        return (
                            <div key={item.label} className="legend-item" onClick={() => lengendClick(item.label)}>

                                {
                                    <div className="point" style={{ backgroundColor: item.backgroundColor?.toString() }}>
                                    </div>
                                }

                                <Typography.Text delete={item.hidden}>
                                    {item.label}
                                </Typography.Text>

                            </div>
                        )
                    })
                }
            </div>
        </div>
    )
}

export default React.memo(DashboardEcapModule)